import { useState } from "react";
import { Buton, cn, MetinAlani } from "@koclukpanel/ui";
import type { Gorev } from "./api";

/**
 * VERI GIRISI — F-STV-04
 *
 * "Buyuk dokunmatik sayac veya numpad + tamamla. Uc saniyeden uzun
 *  surmemeli."
 *
 * ================================================================
 * NEDEN SERBEST METIN ALANI DEGIL
 * ================================================================
 * Sayi klavyesi acmak telefonda ekranin yarisini kapatiyor ve her
 * alanda odak degistirmek gerekiyor. Buyuk +/− dugmeleriyle 18 dogru
 * girmek uc dokunusa iniyor (10'ar ve 1'er adim). Klavye yine de var:
 * alana dokunulunca acilan numpad, cok soru cozen ogrenci icin.
 *
 * Toplam HEDEFI ASARSA sunucu reddediyor (F-STV-05). Arayuz bunu
 * ONCEDEN gosteriyor ki ogrenci gonderip hata almasin — ama engel
 * arayuzde DEGIL sunucuda; burasi yalnizca nezaket.
 */

interface VeriGirisiProps {
  gorev: Gorev;
  onKapat: () => void;
  onKaydet: (veri: {
    correctCount: number;
    wrongCount: number;
    emptyCount: number;
    durationSeconds: number | null;
    difficultyRating: number | null;
    studentNote: string | null;
    tamamlandi: boolean;
  }) => void;
}

export function VeriGirisi({ gorev, onKapat, onKaydet }: VeriGirisiProps) {
  const [dogru, setDogru] = useState(gorev.correctCount ?? 0);
  const [yanlis, setYanlis] = useState(gorev.wrongCount ?? 0);
  const [bos, setBos] = useState(gorev.emptyCount ?? 0);
  const [dakika, setDakika] = useState(
    gorev.durationSeconds ? Math.round(gorev.durationSeconds / 60) : 0,
  );
  const [zorluk, setZorluk] = useState<number | null>(gorev.difficultyRating ?? null);
  /*
    ================================================================
    NOT DA ONCEKI DEGERIYLE ACILIYOR — YOKSA SESSIZCE SILINIYORDU
    ================================================================
    Formdaki alti alanin beşi gorevin mevcut degeriyle aciliyordu;
    not `""` ile aciliyordu. Sunucu ise `studentNote`i KOSULSUZ
    yaziyor (`gorev.service.ts` -> `girisKaydet`:
    `studentNote: giris.studentNote ?? null`).

    Sonuc olculdu (04.09.2026): "Turev konusunu anlamadim" yazip
    kaydeden ogrenci, ayni gorevi "Duzenle" ile acip sayilarini
    duzeltince notu NULL'a doniyordu. Hicbir uyari yok, alan bos
    goründügü icin ogrenci sildigini bilmiyor ve kocu bir daha
    gormuyor. Ogrencinin kocuna yazdigi tek serbest metin bu.
  */
  const [not, setNot] = useState(gorev.studentNote ?? "");

  const toplam = dogru + yanlis + bos;
  const hedef = gorev.targetQuestionCount;
  const asiyor = hedef !== null && toplam > hedef;
  const hedefeUlasti = hedef === null ? toplam > 0 : toplam >= hedef;

  return (
    <div className="bg-notr-0 fixed inset-0 z-50 flex flex-col">
      <header className="border-notr-200 flex items-center justify-between border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold">{gorev.lesson.name}</h2>
          {gorev.topic && <p className="text-notr-600 truncate text-sm">{gorev.topic.name}</p>}
        </div>
        <Buton gorunum="sessiz" onClick={onKapat}>
          Kapat
        </Buton>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {hedef !== null && (
          <p
            className={cn(
              "rounded-alan mb-4 px-3 py-2 text-sm",
              asiyor ? "bg-hata-500/10 text-hata-700" : "bg-notr-100 text-notr-600",
            )}
            role={asiyor ? "alert" : undefined}
          >
            {asiyor
              ? `Girdiğin toplam (${toplam}) hedefi (${hedef}) aşıyor. Hedefi aşan çalışmayı koçuna bildir.`
              : `Hedef ${hedef} soru · girilen ${toplam}`}
          </p>
        )}

        <div className="flex flex-col gap-4">
          <Sayac etiket="Doğru" deger={dogru} onDegis={setDogru} vurgu="basarili" />
          <Sayac etiket="Yanlış" deger={yanlis} onDegis={setYanlis} vurgu="hata" />
          <Sayac etiket="Boş" deger={bos} onDegis={setBos} vurgu="notr" />
          <Sayac etiket="Süre (dakika)" deger={dakika} onDegis={setDakika} adim={5} vurgu="marka" />
        </div>

        {/* F-STV-07 — istege bagli ek alanlar */}
        <fieldset className="mt-6">
          <legend className="text-notr-800 mb-2 text-sm font-medium">
            Zorluk <span className="text-notr-600 font-normal">(isteğe bağlı)</span>
          </legend>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setZorluk(zorluk === n ? null : n)}
                aria-pressed={zorluk === n}
                className={cn(
                  "size-dokunma rounded-alan flex-1 text-base font-medium",
                  zorluk === n
                    ? "bg-marka-600 text-notr-0"
                    : "bg-notr-100 text-notr-700 hover:bg-notr-200",
                )}
              >
                {n}
              </button>
            ))}
          </div>
          <p className="text-notr-600 mt-1 text-xs">1 = çok kolay · 5 = çok zor</p>
        </fieldset>

        <div className="mt-6">
          <MetinAlani
            etiket="Not (isteğe bağlı)"
            value={not}
            onChange={(e) => setNot(e.target.value)}
            rows={3}
            placeholder="Anlamadığın bir yer varsa yaz — koçun görecek."
          />
        </div>
      </div>

      {/*
        Kaydet dugmesi ekranin ALTINDA sabit: telefon tutan elin
        basparmagi oraya ulasir. Ustte olsaydi uzun formda her seferinde
        yukari kaydirmak gerekirdi.
      */}
      <footer className="border-notr-200 border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Buton
          boyut="genis"
          disabled={asiyor}
          onClick={() =>
            onKaydet({
              correctCount: dogru,
              wrongCount: yanlis,
              emptyCount: bos,
              durationSeconds: dakika > 0 ? dakika * 60 : null,
              difficultyRating: zorluk,
              studentNote: not.trim() || null,
              // "Tamamladim" iddiasi gonderiliyor ama NIHAI DURUMU
              // SUNUCU belirliyor: hedefin altinda kalirsa "kismen"
              // yaziliyor (F-STV-02). Arayuz burada yalnizca niyeti
              // bildiriyor.
              tamamlandi: hedefeUlasti,
            })
          }
        >
          {hedefeUlasti ? "Tamamladım" : "Kaydet"}
        </Buton>
        {!hedefeUlasti && toplam > 0 && (
          <p className="text-notr-600 mt-2 text-center text-xs">
            Hedefin altında kaldın; görev "kısmen tamamlandı" olarak kaydedilecek.
          </p>
        )}
      </footer>
    </div>
  );
}

/*
  BASARI TONU `onay-700`. `basarili-500` beyaz uzerinde 3,02 kontrast
  veriyor; AA normal metin 4,5 istiyor. Bu sozluk `role="status"`
  mesajlarini boyuyor — yani okunmasi GEREKEN metin.
*/
const VURGU: Record<string, string> = {
  basarili: "text-onay-700",
  hata: "text-hata-500",
  notr: "text-notr-600",
  marka: "text-marka-700",
};

/**
 * Buyuk dokunmatik sayac.
 *
 * Dugmeler 44px'ten BUYUK (56px): bu ekran yurumeyen, otobuste,
 * tek elle kullanilan bir ekran. Sayinin kendisi de dokunulabilir —
 * dokununca numpad aciliyor ve cok soru cozen ogrenci dogrudan yaziyor.
 */
function Sayac({
  etiket,
  deger,
  onDegis,
  adim = 1,
  vurgu,
}: {
  etiket: string;
  deger: number;
  onDegis: (v: number) => void;
  adim?: number;
  vurgu: keyof typeof VURGU;
}) {
  const degistir = (fark: number): void => onDegis(Math.max(0, deger + fark));

  return (
    <div className="flex items-center gap-3">
      <span className="text-notr-800 w-28 shrink-0 text-sm font-medium">{etiket}</span>

      <button
        type="button"
        onClick={() => degistir(-adim)}
        disabled={deger === 0}
        className="rounded-alan bg-notr-100 text-notr-700 hover:bg-notr-200 size-14 shrink-0 text-2xl font-medium disabled:opacity-40"
      >
        <span className="sr-only">{etiket} azalt</span>
        <span aria-hidden="true">−</span>
      </button>

      {/*
        `Alan` DEGIL — bilincli. Bu bir form alani degil, +/− dugmelerinin
        arasindaki sayi gostergesi: etiketi solda ayri sutunda, yazisi
        `text-2xl`, yuksekligi `py-3`. `Alan`in iki korumasindan da (16px
        yazi, 44px dokunma hedefi) fazlasi zaten var. Bekci testindeki
        muafiyet: `packages/ui/src/form-bileseni.test.ts` → MUAFIYETLER.
      */}
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={deger}
        onChange={(e) => onDegis(Math.max(0, Number(e.target.value) || 0))}
        aria-label={etiket}
        className={cn(
          "rounded-alan bg-notr-0 min-w-0 flex-1 py-3 text-center text-2xl font-semibold tabular-nums",
          "ring-notr-200 focus:ring-marka-600 ring-1 outline-none ring-inset focus:ring-2",
          VURGU[vurgu],
        )}
      />

      <button
        type="button"
        onClick={() => degistir(adim)}
        className="rounded-alan bg-notr-100 text-notr-700 hover:bg-notr-200 size-14 shrink-0 text-2xl font-medium"
      >
        <span className="sr-only">{etiket} artır</span>
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}
